import { CrmEntityType } from '@prisma/client';
import { chunkArray } from '../../utils/arrays';
import { uniqueChunks } from './chunks';
import { backoffMs, isQuarantined, QUARANTINE_REQUEUE_MS, SUCCESS_REQUEUE_MS } from './policy';
import { CrmSyncRecordClient, SyncOutcome } from './types';

type Logger = Pick<Console, 'warn'>;

/** Bounds concurrent single-row updates to protect Prisma's connection pool. */
export const OUTCOME_WRITE_BATCH_SIZE = 100;

function scrubbedError(error: { category?: string; id?: string }): string {
  return `category=${error.category ?? 'UNKNOWN'} id=${error.id ?? 'n/a'}`;
}

/** Transitions below are owned by crm-sync. */
export async function stampAttempted({
  now,
  prisma,
  recordIds,
}: {
  prisma: CrmSyncRecordClient;
  recordIds: number[];
  now: Date;
}): Promise<void> {
  await Promise.all(
    uniqueChunks(recordIds).map(ids =>
      prisma.crmSyncRecord.updateMany({ data: { lastAttemptedAt: now }, where: { id: { in: ids } } }),
    ),
  );
}

export async function stampCreatePending({
  now,
  prisma,
  recordIds,
}: {
  prisma: CrmSyncRecordClient;
  recordIds: number[];
  now: Date;
}): Promise<void> {
  await Promise.all(
    uniqueChunks(recordIds).map(ids =>
      prisma.crmSyncRecord.updateMany({
        data: { createPendingAt: now },
        where: { createPendingAt: null, id: { in: ids } },
      }),
    ),
  );
}

/** Retracts active contact mappings only when a caller has HubSpot evidence that the exact IDs are stale. */
export async function retractStaleContactIds({
  crmObjectIds,
  now,
  prisma,
}: {
  prisma: CrmSyncRecordClient;
  crmObjectIds: string[];
  now: Date;
}): Promise<number> {
  let count = 0;

  for (const ids of uniqueChunks(crmObjectIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: {
        createPendingAt: null,
        crmObjectId: null,
        dueAt: now,
        failureCount: 0,
        isTerminal: false,
        lastError: 'HubSpot rejected this contact ID as a stale invoice association target',
        quarantinedAt: null,
      },
      where: {
        crmObjectId: { in: ids },
        entityType: CrmEntityType.CONTACT,
        isPlatformDeleted: false,
      },
    });
    count += result.count;
  }

  return count;
}

/** Partitions outcomes by payload so identical state transitions are persisted in batches. */
export async function recordOutcomes({
  entityType,
  logger,
  maxBackoffMs,
  now,
  outcomes,
  prisma,
}: {
  prisma: CrmSyncRecordClient;
  outcomes: SyncOutcome[];
  now: Date;
  maxBackoffMs: number;
  entityType: CrmEntityType;
  logger: Logger;
}): Promise<void> {
  for (const outcomeBatch of chunkArray(outcomes, OUTCOME_WRITE_BATCH_SIZE)) {
    await recordOutcomeBatch({ entityType, logger, maxBackoffMs, now, outcomes: outcomeBatch, prisma });
  }
}

async function recordOutcomeBatch({
  entityType,
  logger,
  maxBackoffMs,
  now,
  outcomes,
  prisma,
}: {
  prisma: CrmSyncRecordClient;
  outcomes: SyncOutcome[];
  now: Date;
  maxBackoffMs: number;
  entityType: CrmEntityType;
  logger: Logger;
}): Promise<void> {
  const successes = outcomes.filter(
    (outcome): outcome is Extract<SyncOutcome, { kind: 'success' }> => outcome.kind === 'success',
  );
  const successesWithCrmObjectId = successes.filter(
    (outcome): outcome is typeof outcome & { crmObjectId: string } => outcome.crmObjectId !== undefined,
  );
  const unchangedIds = outcomes.flatMap(outcome => (outcome.kind === 'unchanged' ? [outcome.recordId] : []));
  const selfHeals = outcomes.filter(
    (outcome): outcome is Extract<SyncOutcome, { kind: 'self-heal' }> => outcome.kind === 'self-heal',
  );
  const selfHealIds = selfHeals.flatMap(outcome => (outcome.crmObjectId ? [] : [outcome.recordId]));
  const selfHealsWithCrmObjectId = selfHeals.filter((outcome): outcome is typeof outcome & { crmObjectId: string } =>
    Boolean(outcome.crmObjectId),
  );
  const unresolvableIds = outcomes.flatMap(outcome => (outcome.kind === 'unresolvable' ? [outcome.recordId] : []));
  const failures = outcomes.filter(
    (outcome): outcome is Extract<SyncOutcome, { kind: 'failure' }> => outcome.kind === 'failure',
  );
  const successData = {
    createPendingAt: null,
    dueAt: new Date(now.getTime() + SUCCESS_REQUEUE_MS),
    failureCount: 0,
    lastError: null,
    lastSyncedAt: now,
    quarantinedAt: null,
  };
  const batchedSuccessIdsByTerminality = new Map<boolean | undefined, number[]>([[undefined, unchangedIds]]);

  for (const outcome of successes) {
    if (outcome.crmObjectId !== undefined) continue;

    const ids = batchedSuccessIdsByTerminality.get(outcome.isTerminal) ?? [];
    ids.push(outcome.recordId);
    batchedSuccessIdsByTerminality.set(outcome.isTerminal, ids);
  }

  await Promise.all([
    ...successesWithCrmObjectId.map(outcome =>
      prisma.crmSyncRecord.update({
        data: {
          ...successData,
          crmObjectId: outcome.crmObjectId,
          ...(outcome.isTerminal !== undefined ? { isTerminal: outcome.isTerminal } : {}),
        },
        where: { id: outcome.recordId },
      }),
    ),
    ...[...batchedSuccessIdsByTerminality].flatMap(([isTerminal, recordIds]) =>
      uniqueChunks(recordIds).map(ids =>
        prisma.crmSyncRecord.updateMany({
          data: {
            ...successData,
            ...(isTerminal !== undefined ? { isTerminal } : {}),
          },
          where: { id: { in: ids } },
        }),
      ),
    ),
    ...uniqueChunks(selfHealIds).map(ids =>
      prisma.crmSyncRecord.updateMany({
        data: { crmObjectId: null, dueAt: now, failureCount: 0, quarantinedAt: null },
        where: { id: { in: ids } },
      }),
    ),
    ...selfHealsWithCrmObjectId.map(outcome =>
      prisma.crmSyncRecord.update({
        data: {
          createPendingAt: null,
          crmObjectId: outcome.crmObjectId,
          dueAt: now,
          failureCount: 0,
          lastError: null,
          quarantinedAt: null,
        },
        where: { id: outcome.recordId },
      }),
    ),
    ...uniqueChunks(unresolvableIds).map(ids =>
      prisma.crmSyncRecord.updateMany({
        data: {
          crmObjectId: null,
          isTerminal: true,
          lastError:
            'deletion-only state has no identifying property once its crmObjectId went stale; nothing further to converge',
        },
        where: { id: { in: ids } },
      }),
    ),
    ...failures.map(async outcome => {
      const failureCount = outcome.failureCount + 1;
      const quarantining = isQuarantined(failureCount);

      await prisma.crmSyncRecord.update({
        data: {
          ...(outcome.crmObjectId ? { createPendingAt: null, crmObjectId: outcome.crmObjectId } : {}),
          dueAt: new Date(
            now.getTime() + (quarantining ? QUARANTINE_REQUEUE_MS : backoffMs(failureCount, maxBackoffMs)),
          ),
          failureCount,
          lastError: scrubbedError(outcome.error),
          quarantinedAt: quarantining ? now : undefined,
        },
        where: { id: outcome.recordId },
      });

      logger.warn('CRM sync record failed', {
        category: outcome.error.category,
        entityId: outcome.entityId,
        entityType,
        failureCount,
        id: outcome.error.id,
        message: outcome.error.message,
        quarantined: quarantining,
        recordId: outcome.recordId,
      });
    }),
  ]);
}

export async function markContactsDue({
  now,
  prisma,
  userIds,
}: {
  prisma: CrmSyncRecordClient;
  userIds: string[];
  now: Date;
}): Promise<number> {
  let count = 0;

  for (const ids of uniqueChunks(userIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: { dueAt: now },
      where: { entityId: { in: ids }, entityType: CrmEntityType.CONTACT },
    });
    count += result.count;
  }

  return count;
}
